export function buildClinicOptions(normalizedPatients) {
  const byClinic = new Map();

  (Array.isArray(normalizedPatients) ? normalizedPatients : []).forEach(
    (patientRow) => {
      const clinicId = Number(patientRow?.clinicId);
      if (!Number.isFinite(clinicId) || clinicId <= 0) return;

      if (!byClinic.has(clinicId)) {
        const clinicName = String(patientRow?.clinicName || "").trim();
        byClinic.set(clinicId, clinicName || `Clinique #${clinicId}`);
      }
    }
  );

  return Array.from(byClinic.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
}

export function filterPatientsByClinic(normalizedPatients, selectedClinicId) {
  const patients = Array.isArray(normalizedPatients) ? normalizedPatients : [];
  if (String(selectedClinicId) === "all") return patients;

  const clinicId = Number(selectedClinicId);
  if (!Number.isFinite(clinicId) || clinicId <= 0) return patients;

  return patients.filter((patientRow) => Number(patientRow?.clinicId) === clinicId);
}

export function resolveSelectedPatient({
  clinicScopedPatients,
  selectedPatientId,
  patient,
  selectedClinicId,
}) {
  if (!Array.isArray(clinicScopedPatients) || clinicScopedPatients.length === 0) {
    return String(selectedClinicId) === "all" ? patient ?? null : null;
  }

  if (selectedPatientId != null) {
    const found = clinicScopedPatients.find(
      (patientRow) => String(patientRow?.id) === String(selectedPatientId)
    );
    if (found) return found;
  }

  return patient ?? clinicScopedPatients[0] ?? null;
}

export function getReplacementSelectedPatientId(
  clinicScopedPatients,
  selectedPatientId
) {
  if (String(selectedPatientId) === "all") return null;

  const visiblePatients = Array.isArray(clinicScopedPatients)
    ? clinicScopedPatients
    : [];

  const stillVisible = visiblePatients.some(
    (patientRow) => String(patientRow?.id) === String(selectedPatientId)
  );

  if (stillVisible) return null;
  if (visiblePatients.length > 0) return visiblePatients[0].id;
  return "all";
}

export function buildPaceExportRows({
  isAllSelected,
  selectedPatient,
  filteredAllPatientsList,
  filteredAllPatientsT0T1Rows,
  paceT0T1,
  buildEvaluationExportRow,
  normalizePacePayload,
}) {
  if (isAllSelected) {
    const patientById = new Map(
      (Array.isArray(filteredAllPatientsList) ? filteredAllPatientsList : []).map(
        (patient) => [Number(patient?.id), patient]
      )
    );

    return (Array.isArray(filteredAllPatientsT0T1Rows)
      ? filteredAllPatientsT0T1Rows
      : []
    )
      .map((row) => {
        const patientId = Number(row?.patientId);
        const patientRow = patientById.get(patientId);
        if (!patientRow) return null;

        return buildEvaluationExportRow({
          patient: patientRow,
          t0: normalizePacePayload(row?.t0),
          t1: normalizePacePayload(row?.t1),
          fallbackAge: row?.age,
          fallbackSex: row?.sex,
        });
      })
      .filter(Boolean);
  }

  if (!selectedPatient) return [];

  return [
    buildEvaluationExportRow({
      patient: selectedPatient,
      t0: normalizePacePayload(paceT0T1?.t0),
      t1: normalizePacePayload(paceT0T1?.t1),
    }),
  ];
}

export function buildProgramCsvDataRows({
  isAllSelected,
  filteredAllPatientsList,
  filteredAllPatientsSessions,
  selectedPatient,
  sessions,
  weekCount,
  missingValue,
  buildWeeklyCsvRow,
}) {
  if (isAllSelected) {
    return (Array.isArray(filteredAllPatientsList) ? filteredAllPatientsList : []).map(
      (patient) => {
        const patientId = Number(patient?.id);
        const patientSessions = (
          Array.isArray(filteredAllPatientsSessions)
            ? filteredAllPatientsSessions
            : []
        ).filter((sessionRow) => Number(sessionRow?.__patientId) === patientId);

        return buildWeeklyCsvRow({
          patientId: patient?.id ?? missingValue,
          sessions: patientSessions,
          weekCount,
        });
      }
    );
  }

  return [
    buildWeeklyCsvRow({
      patientId: selectedPatient?.id ?? missingValue,
      sessions,
      weekCount,
    }),
  ];
}

export function buildPatientStatsCsvRows({
  isPace,
  isAllSelected,
  selectedPatient,
  filteredAllPatientsList,
  filteredAllPatientsT0T1Rows,
  paceT0T1,
  filteredAllPatientsSessions,
  sessions,
  weekCount,
  missingValue,
  buildEvaluationExportRow,
  normalizePacePayload,
  buildCsvTemplateRows,
  buildWeeklyCsvRow,
  evalExportHeaders,
}) {
  if (isPace) {
    const paceRows = buildPaceExportRows({
      isAllSelected,
      selectedPatient,
      filteredAllPatientsList,
      filteredAllPatientsT0T1Rows,
      paceT0T1,
      buildEvaluationExportRow,
      normalizePacePayload,
    });

    return [evalExportHeaders, ...paceRows];
  }

  const { row1, row2, row3 } = buildCsvTemplateRows(weekCount);
  const dataRows = buildProgramCsvDataRows({
    isAllSelected,
    filteredAllPatientsList,
    filteredAllPatientsSessions,
    selectedPatient,
    sessions,
    weekCount,
    missingValue,
    buildWeeklyCsvRow,
  });

  return [row1, row2, row3, ...dataRows];
}

export function buildCsvFileName({
  isAllSelected,
  isPace,
  selectedPatient,
  today,
}) {
  if (isAllSelected) {
    const prefix = isPace
      ? "export_evals_all_patients"
      : "tous_les_patients_programme_rxapa";
    return `${prefix}_${today}.csv`;
  }

  const safeFirst = String(selectedPatient?.firstname || "patients").replaceAll(
    /\s+/g,
    "_"
  );
  const safeLast = String(selectedPatient?.lastname || "").replaceAll(/\s+/g, "_");
  const suffix = isPace ? "export_evals" : "programme_rxapa";

  return `${safeFirst}_${safeLast}_${suffix}_${today}.csv`;
}

export function getPaceStatusState(isLoading, isError) {
  if (isLoading) return "loading";
  if (isError) return "error";
  return "idle";
}
